/**
 * What every button DOES, apart from how it looks: the press, `busy`, the `aria-*` props and the host-safe
 * structure (`PressableSurface`: layout on the `<button>`, paint on an inner View). `Button` draws it with
 * its look; `ActionButton` with its legacy one (DESIGN_SYSTEM.md §7.2), so the two share one behaviour.
 */
import React from "react";
import { ActivityIndicator, StyleSheet, Text } from "react-native";

import PressableSurface from "@rista/ui/library/components/PressableSurface";
import type { PressState } from "@rista/ui/library/components/PressableSurface";
import { useTheme } from "@rista/ui/library/theme/ThemeProvider";
import { lookFor } from "@rista/ui/library/theme/variants";
import type { LookSpec, PicksOf } from "@rista/ui/library/theme/variants";

type ButtonLook = LookSpec<"root" | "surface" | "label" | "icon", any>;

export interface ButtonBodyProps<L extends ButtonLook> {
  look: L;
  picks: PicksOf<L>;
  label: string;
  onPress: () => void;
  disabled?: boolean;
  /**
   * Its action is running (`useAction`'s `busy`, ACTIONS.md): shows progress and ignores presses.
   * Not a denial, so it is not drawn disabled and never sets `aria-disabled`.
   */
  busy?: boolean;
  /** Distinguishes controls whose label alone is ambiguous out of context. */
  accessibilityLabel?: string;
  /** Keyboard focus arrived or left: a key that acts on the focused control (the discount sheet's E) */
  onFocus?: () => void;
  onBlur?: () => void;
  testID?: string;
}

const ignorePress = () => {};

export function ButtonBody<L extends ButtonLook>({
  look,
  picks,
  label,
  onPress,
  disabled,
  busy,
  accessibilityLabel,
  onFocus,
  onBlur,
  testID,
}: ButtonBodyProps<L>) {
  const theme = useTheme();
  const at = (state: PressState) => lookFor(look, theme, picks, { ...state, disabled: !!disabled });
  return (
    <PressableSurface
      // busy ignores presses here, not through `disabled`: RNW marks a disabled Pressable `aria-disabled`
      onPress={busy ? ignorePress : onPress}
      disabled={disabled}
      onFocus={onFocus}
      onBlur={onBlur}
      testID={testID}
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel ?? label}
      /**
       * 🔴 `aria-disabled`, not `accessibilityState`. react-native-web 0.21 forwards `aria-*`
       * straight to the DOM and no longer translates `accessibilityState`, so the older prop
       * left a web screen reader announcing a button with no disabled state at all. RN 0.78
       * accepts the ARIA prop and maps it back, so this one form works on both.
       */
      aria-disabled={!!disabled}
      aria-busy={!!busy}
      style={(state) => at(state).root}
      surface={(state) => at(state).surface}
    >
      {(state: PressState) => {
        const resolved = at(state);
        return (
          <>
            <Text style={[resolved.label, busy && styles.hidden]} numberOfLines={1}>
              {label}
            </Text>
            {/* over the hidden label, so a busy button keeps its exact size on both platforms */}
            {busy ? (
              <ActivityIndicator
                style={[StyleSheet.absoluteFill, styles.spinner]}
                size="small"
                color={resolved.icon.color as string}
              />
            ) : null}
          </>
        );
      }}
    </PressableSurface>
  );
}

const styles = StyleSheet.create({
  hidden: { opacity: 0 },
  spinner: { alignItems: "center", justifyContent: "center" },
});
